import React, { useState } from 'react';
import { Button, message } from 'antd';
import type { TCredentialingContractBanner } from 'types/credentialing';
import type { SettingEnum } from 'types/enumerationData';
import { markCredentialingSignatureSigned } from 'api/credentialing';
import { getStateDisplayName } from 'utils/common';
import { SvgAlert } from '../SvgIcons';
import SignContractModal from '../SignContractModal';
import s from './s.module.less';

type Props = {
    contracts: TCredentialingContractBanner[];
    stateList?: SettingEnum[];
    onSigned?: () => void;
};

const NotReceivedBanner = ({ contracts, stateList, onSigned }: Props) => {
    const [signingTarget, setSigningTarget] = useState<TCredentialingContractBanner | null>(null);
    const [confirmLoading, setConfirmLoading] = useState(false);

    if (contracts.length === 0) return null;

    const handleConfirm = async () => {
        if (!signingTarget) return;
        setConfirmLoading(true);
        try {
            const res = await markCredentialingSignatureSigned(
                signingTarget.contractId
                    ? { contractId: signingTarget.contractId }
                    : { panelId: signingTarget.panelId },
            );
            if (res && !res.error) {
                if (res.data?.data === false) {
                    message.warning('We could not confirm this contract. Please refresh and try again.');
                } else {
                    message.success('Contract signed status updated');
                }
                setSigningTarget(null);
                onSigned?.();
            }
        } finally {
            setConfirmLoading(false);
        }
    };

    const single = contracts.length === 1;
    const c0 = contracts[0];

    return (
        <div className={s.wrap}>
            {single ? (
                <div className={s.singleBanner}>
                    <div className={s.singleIcon}><SvgAlert /></div>
                    <div className={s.singleBody}>
                        <div className={s.singleName}>
                            {c0.payerName} has not received your signed contract for {getStateDisplayName(c0.payerState, stateList)}
                        </div>
                        <div className={s.singleDetail}>
                            You marked it signed, but {c0.payerName} has no signed copy. Review it again, check every required field is complete and that it was submitted, then mark it signed.
                        </div>
                    </div>
                    <Button className={s.signBtn} onClick={() => setSigningTarget(c0)}>
                        Review and sign
                    </Button>
                </div>
            ) : (
                <div className={s.multiCard}>
                    <div className={s.multiHeader}>
                        <div className={s.singleIcon}><SvgAlert /></div>
                        <div className={s.multiHeaderBody}>
                            <div className={s.multiTitle}>
                                {contracts.length} signed contracts have not been received
                            </div>
                            <div className={s.multiSubtitle}>
                                These were marked signed, but the payers have no signed copy. Review each one, check every required field is complete and that it was submitted, then mark it signed.
                            </div>
                        </div>
                    </div>
                    {contracts.map((c, i) => (
                        <div key={c.contractId || c.panelId} className={`${s.multiRow}${i > 0 ? ` ${s.withBorder}` : ''}`}>
                            <div className={s.rowLabel}>
                                {c.payerName} <span className={s.rowState}>&middot; {getStateDisplayName(c.payerState, stateList)}</span>
                            </div>
                            <Button className={s.signBtn} onClick={() => setSigningTarget(c)}>
                                Review and sign
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <SignContractModal
                open={!!signingTarget}
                payerName={signingTarget?.payerName || ''}
                state={getStateDisplayName(signingTarget?.payerState, stateList) || ''}
                signingUrl={signingTarget?.signUrl}
                confirmLoading={confirmLoading}
                onClose={() => setSigningTarget(null)}
                onConfirm={handleConfirm}
            />
        </div>
    );
};

export default NotReceivedBanner;
